import { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import AgentLayout from "@/layouts/AgentLayout";
import { useGetAgentProfile, useUpdateAgentProfile, useGetLanguages, useGetStates, useGetLocalities, API_BASE } from "@/lib/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import {
  Check, Loader2, Save, User, Award, Briefcase, MapPin, Building2,
  Settings as SettingsIcon, Palette, Sun, Moon, ChevronDown, Search
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const DEFAULT_US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY", "DC",
];

function MultiSelect({
  options = [], value = [], onChange, placeholder, maxHeight = 260,
}: {
  options: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; maxHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = useMemo(() => {
    return (options || []).map((o: any) => {
      if (typeof o === "string") return { value: o, label: o };
      if (typeof o === "object" && o !== null) {
        const val = String(o.value ?? o.id ?? o.code ?? o.name ?? "");
        const rawLbl = o.label ?? o.name ?? o.localityName ?? o.value ?? val;
        const lbl = typeof rawLbl === "object" && rawLbl !== null
          ? String(rawLbl.name || rawLbl.code || "Option")
          : String(rawLbl || val || "Option");
        return { value: val, label: lbl };
      }
      return { value: String(o ?? ""), label: String(o ?? "") };
    });
  }, [options]);

  const filtered = useMemo(() => {
    return normalizedOptions.filter(o =>
      String(o.label).toLowerCase().includes((search || "").toLowerCase())
    );
  }, [normalizedOptions, search]);

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    const strVal = String(optValue);
    onChange(safeValue.includes(strVal) ? safeValue.filter(v => v !== strVal) : [...safeValue, strVal]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[44px] px-3 py-2 border border-input rounded-xl bg-background text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 transition-colors shadow-2xs"
        >
          {safeValue.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            safeValue.map((v, idx) => {
              const foundLabel = normalizedOptions.find(o => o.value === v)?.label;
              const label = typeof foundLabel === 'string' ? foundLabel : String(v || idx);
              return (
                <Badge key={`${v}-${idx}`} variant="secondary" className="text-xs rounded-lg px-2 py-0.5 font-medium">
                  {label}
                  <span
                    className="ml-1.5 cursor-pointer hover:text-destructive font-bold"
                    onClick={e => { e.stopPropagation(); toggle(v); }}
                  >
                    &times;
                  </span>
                </Badge>
              );
            })
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0 rounded-2xl border border-border shadow-xl bg-popover" align="start">
        <div className="p-2 border-b border-border bg-muted/30">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs rounded-xl border-border bg-background"
            />
          </div>
        </div>
        <div className="p-1 overflow-y-auto" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground text-center">No options found.</div>
          ) : (
            filtered.map(opt => {
              const selected = safeValue.includes(opt.value);
              return (
                <div
                  key={opt.value}
                  className="flex items-center gap-2 px-3 py-2 text-xs rounded-xl hover:bg-muted cursor-pointer transition-colors"
                  onClick={() => toggle(opt.value)}
                >
                  <div className={cn(
                    "w-4 h-4 rounded border flex items-center justify-center transition-colors",
                    selected ? "bg-primary border-primary text-white" : "border-input"
                  )}>
                    {selected && <Check className="w-3 h-3" />}
                  </div>
                  <span className="font-medium text-foreground">{opt.label}</span>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive mt-1">{message}</p>;
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-sm font-medium text-foreground">
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-border">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="font-semibold text-lg">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

export default function AgentSettings() {
  const { theme, setTheme } = useTheme();
  const { data: profile, isLoading } = useGetAgentProfile();
  const updateMutation = useUpdateAgentProfile();

  const { data: statesData = [] } = useGetStates();
  const US_STATES = useMemo(() => {
    if (!statesData || !Array.isArray(statesData) || statesData.length === 0) return DEFAULT_US_STATES;
    return statesData.map((s: any) => typeof s === 'string' ? s : String(s?.code || s?.name || s?.id || '')).filter(Boolean);
  }, [statesData]);

  const { data: languagesData = [] } = useGetLanguages();
  const LANGUAGES = useMemo(() => {
    return languagesData.map((l: any) => typeof l === 'string' ? l : String(l?.name || l?.code || ''));
  }, [languagesData]);

  const { register, control, reset, handleSubmit, watch, setValue, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      firstName: "", middleName: "", lastName: "", displayName: "",
      email: "",
      npnNumber: "", licenseNumber: "", experience: "",
      agencyName: "", licensedStates: [], languages: [], localityIds: [],
      phone: "", whatsapp: "",
      buildingNo: "", street1: "", street2: "", city: "", state: "", zipCode: ""
    }
  });

  const selectedState = watch("state");
  const { data: localitiesData = [] } = useGetLocalities(selectedState);
  const LOCALITIES = useMemo(() => {
    return (localitiesData || []).map((loc: any) => ({
      value: String(loc.id),
      label: `${loc.name} (${loc.state}${loc.city ? ' - ' + loc.city : ''})`
    }));
  }, [localitiesData]);

  useEffect(() => {
    if (profile) {
      const pAddress = profile.addresses?.find((a: any) => a.isPrimary) || {};
      const agentState = pAddress.state || (profile.agent?.licensedStates && profile.agent.licensedStates[0]) || "";
      const currentLocalityIds = profile.localities?.map((l: any) => String(l.id)) || [];

      reset({
        firstName: profile.firstName || "",
        middleName: profile.middleName || "",
        lastName: profile.lastName || "",
        displayName: profile.displayName || "",
        email: profile.email || "",
        npnNumber: profile.agent?.npnNumber || "",
        licenseNumber: profile.agent?.licenseNumber || "",
        experience: profile.agent?.experience || "",
        agencyName: profile.agent?.agencyName || "",
        licensedStates: profile.agent?.licensedStates || (agentState ? [agentState] : []),
        localityIds: currentLocalityIds,
        languages: profile.languages?.map((l: any) => typeof l === 'string' ? l : l.name) || [],
        phone: profile.phone || "",
        whatsapp: profile.whatsapp || "",
        buildingNo: pAddress.buildingNo || "",
        street1: pAddress.street1 || "",
        street2: pAddress.street2 || "",
        city: pAddress.city || "",
        state: agentState,
        zipCode: pAddress.zipCode || "",
      });
    }
  }, [profile, reset]);

  const displayName = watch("displayName") || (watch("firstName") ? `${watch("firstName")} ${watch("lastName")}` : "Agent");
  const fallbackChars = displayName ? displayName.substring(0, 2).toUpperCase() : "AG";

  const onSubmit = (data: any) => {
    updateMutation.mutate(data, {
      onSuccess: () => {
        toast.success("Profile updated successfully!");
        reset(data);
        const u = localStorage.getItem("user");
        if (u) {
          const parsed = JSON.parse(u);
          parsed.displayName = data.displayName;
          parsed.firstName = data.firstName;
          parsed.lastName = data.lastName;
          localStorage.setItem("user", JSON.stringify(parsed));
        }
      },
      onError: (err: any) => {
        toast.error(err.message || "Failed to update profile");
      }
    });
  };

  if (isLoading) {
    return (
      <AgentLayout>
        <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 space-y-6">
          <Skeleton className="w-1/3 h-10 rounded-xl" />
          <Skeleton className="w-full h-[500px] rounded-2xl" />
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-2">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-2xl flex-shrink-0">
              <SettingsIcon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Profile Settings</h1>
              <p className="text-sm text-muted-foreground">Manage your agent credentials and account preferences</p>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            className="bg-primary hover:bg-primary/90 text-white gap-2 rounded-xl shadow-md shadow-primary/20 min-w-[140px]"
            disabled={updateMutation.isPending || !isDirty}
          >
            {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {updateMutation.isPending ? "Saving..." : "Save All Changes"}
          </Button>
        </div>

        {/* Tabbed Steps Navigation */}
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="bg-muted/80 rounded-2xl mb-6 flex-wrap h-auto gap-1.5 p-1.5 border border-border/50">
            {[
              { id: "profile", icon: User, label: "Basic Info" },
              { id: "professional", icon: Award, label: "Professional" },
              { id: "agency", icon: Briefcase, label: "Agency & Coverage" },
              { id: "address", icon: MapPin, label: "Contact & Address" },
              { id: "appearance", icon: Palette, label: "Appearance" },
            ].map(s => (
              <TabsTrigger key={s.id} value={s.id} className="rounded-xl gap-2 text-sm px-4 py-2 font-medium transition-all" data-testid={`tab-${s.id}`}>
                <s.icon className="w-4 h-4" />
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <form onSubmit={handleSubmit(onSubmit)}>

            {/* ── TAB 1: BASIC INFO ── */}
            <TabsContent value="profile">
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="bg-card rounded-2xl border border-border p-6 sm:p-8 space-y-6 shadow-xs">
                <SectionHeader icon={User} title="Basic Information" subtitle="Your name and personal details" />

                {/* Avatar Header */}
                <div className="flex items-center gap-4 pb-4 border-b border-border">
                  <Avatar className="w-16 h-16 ring-4 ring-primary/10">
                    <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white text-xl font-bold">
                      {fallbackChars}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-bold text-lg text-foreground">{displayName}</h3>
                    <Badge variant="outline" className="mt-1 bg-primary/10 text-primary border-primary/20 font-medium">Licensed Agent</Badge>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>First Name</FieldLabel>
                    <Input {...register("firstName", { required: "First name is required" })} className="mt-1.5 rounded-xl h-11" />
                    <FieldError message={errors.firstName?.message as string} />
                  </div>
                  <div>
                    <FieldLabel>Middle Name</FieldLabel>
                    <Input {...register("middleName")} className="mt-1.5 rounded-xl h-11" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>Last Name</FieldLabel>
                    <Input {...register("lastName", { required: "Last name is required" })} className="mt-1.5 rounded-xl h-11" />
                    <FieldError message={errors.lastName?.message as string} />
                  </div>
                  <div>
                    <FieldLabel required>Display Name</FieldLabel>
                    <Input {...register("displayName", { required: "Display name is required" })} className="mt-1.5 rounded-xl h-11" />
                    <FieldError message={errors.displayName?.message as string} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Email Address</FieldLabel>
                    <Input {...register("email")} disabled className="mt-1.5 bg-muted rounded-xl h-11" />
                    <p className="text-xs text-muted-foreground mt-1">Email address cannot be modified.</p>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-border">
                  <Button type="submit" disabled={updateMutation.isPending || !isDirty} className="rounded-xl gap-2 min-w-[130px]">
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </TabsContent>

            {/* ── TAB 2: PROFESSIONAL ── */}
            <TabsContent value="professional">
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="bg-card rounded-2xl border border-border p-6 sm:p-8 space-y-6 shadow-xs">
                <SectionHeader icon={Award} title="Professional Details" subtitle="Your agent credentials and state licenses" />

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>NPN Number</FieldLabel>
                    <Input {...register("npnNumber", { required: "NPN is required" })} className="mt-1.5 rounded-xl h-11" />
                    <FieldError message={errors.npnNumber?.message as string} />
                  </div>
                  <div>
                    <FieldLabel required>State License Number</FieldLabel>
                    <Input {...register("licenseNumber", { required: "License number is required" })} className="mt-1.5 rounded-xl h-11" />
                    <FieldError message={errors.licenseNumber?.message as string} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Years of Experience</FieldLabel>
                    <Controller
                      name="experience" control={control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="mt-1.5 rounded-xl h-11"><SelectValue placeholder="Select years" /></SelectTrigger>
                          <SelectContent className="rounded-xl">
                            {["Less than 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"].map(y => (
                              <SelectItem key={y} value={y}>{y}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-border">
                  <Button type="submit" disabled={updateMutation.isPending || !isDirty} className="rounded-xl gap-2 min-w-[130px]">
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </TabsContent>

            {/* ── TAB 3: AGENCY & COVERAGE ── */}
            <TabsContent value="agency">
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="bg-card rounded-2xl border border-border p-6 sm:p-8 space-y-6 shadow-xs">
                <SectionHeader icon={Briefcase} title="Agency & Coverage" subtitle="Where you work and languages you speak" />

                <div>
                  <FieldLabel required>Agency / Company Name</FieldLabel>
                  <Input {...register("agencyName", { required: "Agency name is required" })} className="mt-1.5 rounded-xl h-11" />
                  <FieldError message={errors.agencyName?.message as string} />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>State</FieldLabel>
                    <div className="mt-1.5">
                      <Controller
                        name="state" control={control} rules={{ required: "State is required" }}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={(val) => {
                            field.onChange(val);
                            setValue("licensedStates", [val], { shouldDirty: true });
                            setValue("localityIds", [], { shouldDirty: true });
                          }}>
                            <SelectTrigger className="rounded-xl h-11"><SelectValue placeholder="Select state..." /></SelectTrigger>
                            <SelectContent className="max-h-60 rounded-xl">
                              {US_STATES.map((s, idx) => (
                                <SelectItem key={`${s}-${idx}`} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    {errors.state && <p className="text-xs text-destructive mt-1">State is required</p>}
                  </div>

                  <div>
                    <FieldLabel required>Locality</FieldLabel>
                    <div className="mt-1.5">
                      <Controller
                        name="localityIds" control={control} rules={{ required: "Locality is required" }}
                        render={({ field }) => (
                          <MultiSelect
                            options={LOCALITIES}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={selectedState ? "Select locality..." : "Select state first"}
                          />
                        )}
                      />
                    </div>
                    {errors.localityIds && <p className="text-xs text-destructive mt-1">Locality is required</p>}
                  </div>

                  <div className="sm:col-span-2">
                    <FieldLabel>Languages Spoken</FieldLabel>
                    <div className="mt-1.5">
                      <Controller
                        name="languages" control={control}
                        render={({ field }) => (
                          <MultiSelect options={LANGUAGES} value={field.value} onChange={field.onChange} placeholder="Select languages..." />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-border">
                  <Button type="submit" disabled={updateMutation.isPending || !isDirty} className="rounded-xl gap-2 min-w-[130px]">
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </TabsContent>

            {/* ── TAB 4: CONTACT & ADDRESS ── */}
            <TabsContent value="address">
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="bg-card rounded-2xl border border-border p-6 sm:p-8 space-y-6 shadow-xs">
                <SectionHeader icon={MapPin} title="Contact & Address" subtitle="Phone numbers and mailing location" />

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>Phone Number</FieldLabel>
                    <Input
                      {...register("phone", {
                        required: "Phone number is required",
                        pattern: { value: /^[^a-zA-Z]+$/, message: "Alphabets are not allowed" }
                      })}
                      onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                      type="tel" className="mt-1.5 rounded-xl h-11"
                    />
                    <FieldError message={errors.phone?.message as string} />
                  </div>
                  <div>
                    <FieldLabel>WhatsApp Number</FieldLabel>
                    <Input
                      {...register("whatsapp", {
                        pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                      })}
                      onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                      type="tel" className="mt-1.5 rounded-xl h-11"
                    />
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <h3 className="font-semibold flex items-center gap-2 text-foreground text-sm">
                    <Building2 className="w-4 h-4 text-primary" /> Mailing Address
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-5">
                    <div>
                      <FieldLabel>Building No.</FieldLabel>
                      <Input {...register("buildingNo")} className="mt-1.5 rounded-xl h-11" />
                    </div>
                    <div className="sm:col-span-2">
                      <FieldLabel required>Street Name 1</FieldLabel>
                      <Input {...register("street1", { required: "Street name is required" })} className="mt-1.5 rounded-xl h-11" />
                      <FieldError message={errors.street1?.message as string} />
                    </div>
                  </div>

                  <div>
                    <FieldLabel>Street Name 2</FieldLabel>
                    <Input {...register("street2")} className="mt-1.5 rounded-xl h-11" />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-5">
                    <div>
                      <FieldLabel required>City</FieldLabel>
                      <Input {...register("city", { required: "City is required" })} className="mt-1.5 rounded-xl h-11" />
                      <FieldError message={errors.city?.message as string} />
                    </div>
                    <div>
                      <FieldLabel required>State</FieldLabel>
                      <Controller
                        name="state" control={control} rules={{ required: "State is required" }}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="mt-1.5 rounded-xl h-11"><SelectValue placeholder="State" /></SelectTrigger>
                            <SelectContent className="max-h-60 rounded-xl">
                              {US_STATES.map((s, idx) => <SelectItem key={`${s}-${idx}`} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError message={errors.state?.message as string} />
                    </div>
                    <div>
                      <FieldLabel required>Zip Code</FieldLabel>
                      <Input {...register("zipCode", { required: "Zip Code is required" })} className="mt-1.5 rounded-xl h-11" />
                      <FieldError message={errors.zipCode?.message as string} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-border">
                  <Button type="submit" disabled={updateMutation.isPending || !isDirty} className="rounded-xl gap-2 min-w-[130px]">
                    {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Changes
                  </Button>
                </div>
              </motion.div>
            </TabsContent>

            {/* ── TAB 5: APPEARANCE ── */}
            <TabsContent value="appearance">
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }} className="bg-card rounded-2xl border border-border p-6 sm:p-8 space-y-6 shadow-xs">
                <SectionHeader icon={Palette} title="Theme Preferences" subtitle="Customize the interface appearance" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
                  <button
                    type="button"
                    onClick={() => setTheme("light")}
                    className={cn(
                      "p-5 rounded-2xl border flex flex-col items-center justify-center gap-3 transition-all duration-200 cursor-pointer",
                      theme === "light"
                        ? "border-primary bg-primary/10 text-primary shadow-md shadow-primary/10 ring-2 ring-primary/20"
                        : "border-border hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center text-amber-500">
                      <Sun className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-sm">Light Mode</p>
                      <p className="text-xs opacity-75 mt-0.5">Bright, clear visual theme</p>
                    </div>
                    {theme === "light" && <Badge className="bg-primary text-white text-[10px] px-2 py-0.5">Active</Badge>}
                  </button>

                  <button
                    type="button"
                    onClick={() => setTheme("dark")}
                    className={cn(
                      "p-5 rounded-2xl border flex flex-col items-center justify-center gap-3 transition-all duration-200 cursor-pointer",
                      theme === "dark"
                        ? "border-primary bg-primary/10 text-primary shadow-md shadow-primary/10 ring-2 ring-primary/20"
                        : "border-border hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <div className="w-12 h-12 rounded-full bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                      <Moon className="w-6 h-6" />
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-sm">Dark Mode</p>
                      <p className="text-xs opacity-75 mt-0.5">Sleek, low-light theme</p>
                    </div>
                    {theme === "dark" && <Badge className="bg-primary text-white text-[10px] px-2 py-0.5">Active</Badge>}
                  </button>
                </div>
              </motion.div>
            </TabsContent>

          </form>
        </Tabs>
      </div>
    </AgentLayout>
  );
}
